import { useEffect, useState } from 'react'
import { Button, Chip, Spinner, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { PiChatCircleText } from 'react-icons/pi'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import GoBack from '../components/GoBack'
import SharedExperienceRoleBox from '../components/SharedExperienceRoleBox'
import SharedAgamiCard from '../components/SharedAgamiCard'
import NoUploads from '../components/NoUploads'
import { usePageTitle } from '../hooks/toolkit'
import { useAccessibleExperiences } from '../hooks/useAccessibleExperiences'
import { useAgamiRoutes } from '../hooks/useAgamiRoutes'
import { useFeatureFlag } from '../hooks/authorization'
import { CAPABILITIES } from '../utils/constants/capabilities.constants'
import { FEATURE_FLAGS } from '../utils/constants/features.constants'
import { getExperienceSpots } from '../utils/helpers/user-spot.helpers'
import { roleLabel } from '../utils/helpers/sharing.helpers'
import { AgamiSpot } from '../types/supabase'

/**
 * An experience someone shared with you: what is in it, and what you may do.
 *
 * ⚠️ THE GUARD BELOW IS UX, NOT SECURITY. It stops you landing on an experience
 * you have no link to, and it states the intent in code — but it does not make
 * that data unreachable, because it already isn't: SELECT on experiences,
 * user_experiences, geoxp_spots and experience_content_spots is `using (true)`,
 * and the audio bucket is readable by any authenticated user. Anyone with a
 * session can already reach any experience's media without passing through here.
 * That is a platform-wide surface, tracked in stories 10.5 / 10.6 and deliberately
 * not fixed in this cycle. Do not read this guard as a boundary.
 */
const SharedExperience = () => {
  const { t } = useTranslation()
  const { experienceId } = useParams()
  usePageTitle('Shared experience | Agami Dashboard')

  const { experiences, isLoading: loadingExperiences } = useAccessibleExperiences()
  const [spots, setSpots] = useState<AgamiSpot[] | null>(null)
  const [loadingSpots, setLoadingSpots] = useState(true)

  // The experience comes from the list the user can reach, so the check is
  // applicative (AC-10) and `role` arrives with it: no second request for the
  // box or the chip.
  const experience = experiences?.find((exp) => exp.id === experienceId)
  const isReachable = !loadingExperiences && !!experience
  // Same map as the card chip and the box: one role, one wording (02.2).
  const headerRoleLabel = roleLabel(experience?.role)

  const navigate = useNavigate()
  const { proposeAgamiPath } = useAgamiRoutes()
  const ondeFlag = useFeatureFlag(FEATURE_FLAGS.ONDE)
  // The one write action a suggester has here (06.4): present with the
  // CAPABILITY, absent otherwise — and only with the flag, because propose mode
  // exists only in the v2 form. The capability arrives computed server-side
  // with the experience (01.7): no ownership comparison in the client.
  const canPropose = ondeFlag && Boolean(experience?.capabilities?.includes(CAPABILITIES.SUBMIT_DRAFT))

  useEffect(() => {
    if (!isReachable || !experienceId) {
      return
    }

    let cancelled = false

    getExperienceSpots({ experienceId }).then(({ spots: fetched }) => {
      if (cancelled) return
      setSpots(fetched ?? [])
      setLoadingSpots(false)
    })

    return () => {
      cancelled = true
    }
  }, [isReachable, experienceId])

  if (loadingExperiences) {
    return <Spinner className="h-6 w-6" />
  }

  if (!experience) {
    return (
      <div className="w-full py-8 text-center" data-testid="shared-experience-denied">
        <div className="mb-4 flex justify-start">
          <GoBack />
        </div>
        <Typography className="dark:text-gray-200">{t('sharedExperience.notAvailable')}</Typography>
      </div>
    )
  }

  return (
    <>
      <div className="w-full flex flex-col gap-4 py-4">
        <div className="flex items-center gap-2">
          <GoBack />
          <Typography variant="h1" className="text-2xl md:text-4xl" data-testid="shared-experience-name">
            {experience.name}
          </Typography>
          {headerRoleLabel && (
            <Chip
              value={headerRoleLabel}
              size="sm"
              variant="ghost"
              color="blue-gray"
              data-testid="shared-experience-role-chip"
            />
          )}
        </div>

        <SharedExperienceRoleBox role={experience.role} />

        {canPropose && (
          <div>
            <Button
              color={'primary' as colors}
              size="sm"
              className="flex justify-center items-center gap-2"
              onClick={() =>
                navigate(proposeAgamiPath(experienceId!), {
                  state: { returnTo: `/shared-experiences/${experienceId}` },
                })
              }
              data-testid="propose-agami-btn"
            >
              <PiChatCircleText className="h-4 w-4" />
              {t('sharedExperience.proposeAgami')}
            </Button>
          </div>
        )}
      </div>

      <Typography
        variant="h2"
        className="text-xl md:text-2xl py-4"
        data-testid="shared-experience-agami-title"
      >
        {t('sharedExperience.publishedAgami', { count: spots?.length ?? 0 })}
      </Typography>

      <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {loadingSpots && <Spinner className="h-6 w-6" />}

        {!loadingSpots && spots?.length === 0 && (
          <div className="w-full col-span-3 text-center" data-testid="shared-experience-no-agami">
            <NoUploads>
              <Typography className="dark:text-gray-200">{t('sharedExperience.noAgami')}</Typography>
            </NoUploads>
          </div>
        )}

        {!loadingSpots &&
          spots?.map((spot) => (
            <div key={spot.id} className="flex-1 mb-4 basis-1/4">
              <SharedAgamiCard spot={spot} />
            </div>
          ))}
      </div>

      {/*
        Two grafts are deliberately missing here, and both wait on other epics:

        - the "proposals from your kiosk" section (T4/T7) needs the satellite
          table of epic 05: its RLS is owner-only, so it can only be read through
          an edge function that does not exist yet.
        - the "propose an agami" button (T8) needs the v2 form of 06.4 to accept
          a preselected destination.

        Both are gated on the `submit_draft` capability, which already arrives in
        `experience.capabilities`. They are development dependencies, not release
        ones: the cycle ships together.
      */}
    </>
  )
}

export default SharedExperience
